import { defineStore } from 'pinia';
import { shallowRef } from 'vue';
import RAGServiceV2 from '@/services/rag_v2';
import { parseMarkdownArrayToString } from '@/utils/markdown';
import {
  ResourceDraftType,
  MarkdownPath,
  TitlePath,
} from '@/components/knowledgeBase/MDDocumentAdapter';
import { get, set, isEqual, merge as lodashMerge } from 'lodash';
import { useBotsStore } from '@/stores/bots';
import { useIntentsStore } from '@/stores/intents';
import { useHubUiStore } from '@/stores/hubUi';

const StateFlags = {
  Initialized: 1<<0,
};

interface KnowledgeBaseState {
  /**
   * Resources for multiple bots: { [botId]: { [docId]: resource } }.
   * NOT deeply reactive — see {@link commitResources} for how to change it.
   */
  resources: Record<string, Record<string, any>>;
  /**
   * Drafts for resources/resource content: { [botId]: { [docId]: draft } }.
   * Contains new and edits to existing resources.
   */
  resourceDrafts: Record<string, Record<string, any>>;
  /** A list of resources that are queued for deletion when you save changes */
  queuedDeletions: Array<{ botId: string; docId: string }>;
  /** A list of resource connections that are queued for alteration when you save changes */
  queuedIntentStates: Array<{ botId: string; intent: string; state: { state: string; forward?: string; active: boolean } }>;
  /** Various internal state flags from {@link StateFlags} */
  flags: number;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/knowledgeBase.ts` (namespace
 * `knowledgeBase`). Partially persisted to `localStorage` — ONLY `resourceDrafts` and
 * `markdownDrafts`* top-level props — matches the legacy `vuexLocal` reducer's
 * `knowledgeBase: { resourceDrafts: state.knowledgeBase.resourceDrafts, markdownDrafts:
 * state.knowledgeBase.markdownDrafts }` entry in `src/store/index.ts`.
 *
 * (* Note: this module's actual state has no top-level `markdownDrafts` prop today — Markdown
 * content lives inline on each resource/draft object under `resources`/`resourceDrafts`. The
 * `markdownDrafts` persistence key is kept in the `vuexPersist` config below only because it is
 * part of the legacy persisted-key contract (see DESIGN.md hard constraint #1) — it is a no-op
 * pick today (`$patch`/write-back will just find nothing under that key), preserved for byte-for-byte
 * compatibility with whatever old-hub / legacy code may still write to it.)
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - Legacy had BOTH a state prop `resources` and an identity-passthrough getter `resources`. The
 *   getter is renamed `resourcesGetter` (read the state prop directly via `.resources` instead).
 * - Legacy had BOTH a state prop `resourceDrafts` and an identity-passthrough getter
 *   `resourceDrafts`. The getter is renamed `resourceDraftsGetter` (read the state prop directly
 *   via `.resourceDrafts` instead).
 * - No action/mutation name collisions exist in this module (all mutation names are distinct from
 *   action names), so every mutation below becomes a same-named action with no merging needed.
 *
 * Cross-namespace references converted to sibling-store composables (called inside the
 * getter/action body, per DESIGN.md):
 * - `rootGetters['bots/currentBotId']` / `rootGetters['bots/currentBot']` → `useBotsStore()`
 * - `rootGetters['intents/intent']` / `commit('intents/setIntent', ...)` → `useIntentsStore()`
 * - `commit('hubUi/addAlert', ...)` → `useHubUiStore()`
 */
export const useKnowledgeBaseStore = defineStore('knowledgeBase', {
  state: (): KnowledgeBaseState => ({
    /**
     * Resources for multiple bots.
     *
     * A `shallowRef`, so the per-bot maps and the resource objects are plain objects: bots can
     * have tens of thousands of resources, and making (and filling) all of them deeply reactive
     * froze the tab (#2595). Only replacing the whole value notifies consumers — change it
     * through the actions below, never by writing into a resource in place.
     */
    resources: shallowRef({}) as unknown as KnowledgeBaseState['resources'],
    /**
     * Drafts for resources/resource content.
     * Contains new and edits to existing resources.
     */
    resourceDrafts: {},
    /**
     * A list of resources that are queued for deletion when you save changes
     * @type {{botId: string, docId: string}[]}
     */
    queuedDeletions: [],
    /**
     * A list of resource connections that are queued for alteration when you save changes
     * @type {Array<{botId: string, intent: string, state: {state: string, forward?: string, active: boolean}}>}
     */
    queuedIntentStates: [],
    /**
     * Various internal state flags from {@link StateFlags}
     */
    flags: 0,
  }),
  getters: {
    isInitialized: (state: KnowledgeBaseState) => !!(state.flags & StateFlags.Initialized),
    /**
     * Returns a map of all resources
     *
     * Renamed from legacy getter `resources` — collided with the state prop of the same name
     * (identity passthrough). Consumers can also just read `.resources` directly.
     * @param state
     * @returns {Record<string, Record<string, object>>}
     */
    resourcesGetter: (state: KnowledgeBaseState) => state.resources,
    /**
     * Returns a map of all draft resources
     *
     * Renamed from legacy getter `resourceDrafts` — collided with the state prop of the same name
     * (identity passthrough). Consumers can also just read `.resourceDrafts` directly.
     * @param state
     * @returns {Record<string, Record<string, object>>}
     */
    resourceDraftsGetter: (state: KnowledgeBaseState) => state.resourceDrafts,
    /**
     * Get a resource by its Document ID
     * @param state
     * @returns {(botId: string, docId: string) => RAGResourceOverview | null}
     */
    getResource: (state: KnowledgeBaseState) => (botId: string, docId: string) => {
      return state.resources?.[botId]?.[docId] || null;
    },
    /**
     * Return a list of all resources for a bot
     * @param state
     * @returns {(botId: string, resourceIds?: string[]) => RAGResourceOverview[]} Function will return an iterable list of resource objects.
     * Optionally, it will return only the resources with the given IDs that it can find.
     */
    getResources: (state: KnowledgeBaseState) => (botId: string, resourceIds?: string[]) => {
      const resources: any[] = Object.values(state.resources?.[botId] ?? {});
      if (resourceIds?.length) return resources.filter(resource => resourceIds.includes(resource._id));
      return resources;
    },
    /**
     * Get the Markdown content for an input resource
     * @param state
     * @returns {(botId: string, docId: string) => string | null} Function will return `null` if nothing was found
     */
    getMarkdown: (state: KnowledgeBaseState) => (botId: string, docId: string) => {
      const resource = state.resources?.[botId]?.[docId];
      if (!resource) return null;
      return get(resource, (MarkdownPath as any)[resource.type], null);
    },
    /**
     * Get the Markdown content of a draft resource
     * @returns {(resource: {type: RAGResourceType} & Object) => string | null} Function will return `null` if nothing was found
     */
    getMarkdownOfResource: () => (resource: any) => {
      return get(resource, (MarkdownPath as any)[resource.type + ''], null)
    },
    /**
     * Gets the markdown content draft for a resource, if there is any
     * @param state
     * @returns {(botId: string) => ({_id: string, _type: ResourceDraftType} & Object)[]} Function will return an iterable list of resource objects
     */
    getResourceDrafts: (state: KnowledgeBaseState) => (botId: string) => {
      return Object.values(state.resourceDrafts?.[botId] ?? {});
    },
    /**
     * Gets the markdown content draft for a resource, if there is any
     * @param state
     * @returns {(botId: string, docId: string) => ({_id: string, _type: ResourceDraftType} & Object) | null} Function will return null if nothing was found
     */
    getResourceDraft: (state: KnowledgeBaseState) => (botId: string, docId: string) => {
      return state.resourceDrafts?.[botId]?.[docId] || null;
    },
    /**
     * A flat `{ [docId]: draft }` map of all drafts for the *current* bot, keyed by resource ID.
     * Used by the resource table helpers to cheaply hot-swap a published resource for its draft
     * version when rendering (title/content/state). Vuex cached this getter; Pinia getters are
     * cached the same way, so the lookup is effectively O(1) per row even with thousands of
     * resources.
     *
     * Converted from legacy getter signature `(state, _getters, _rootState, rootGetters)` — the
     * `rootGetters['bots/currentBotId']` lookup is now `useBotsStore().currentBotId`, called
     * inside the getter body.
     * @returns {Record<string, object>}
     */
    cachedDraftMap(): Record<string, any> {
      const botId = useBotsStore().currentBotId;
      // Legacy read `rootGetters['bots/currentBotId']` untyped (could be null); this cast
      // preserves that same tolerance — `state.resourceDrafts?.[null]` was always `undefined`.
      return this.resourceDrafts?.[botId as string] ?? {};
    },
    /**
     * Checks if the resource is modified somehow
     *
     * Converted from legacy getter signature `(state, getters)` — local `getters.getResourceDraft`
     * / `getters.getResource` calls are now `this.getResourceDraft` / `this.getResource`.
     * @returns {(botId: string, docId: string, fields?: string[]) => boolean}
     */
    isResourceModified(): (botId: string, docId: string, fields?: string[]) => boolean {
      return (botId: string, docId: string, fields?: string[]) => {
        if (!fields) fields = [
          'metaData',
          'intents',
          'status',
          'content',
        ];

        const draft = JSON.parse(JSON.stringify(this.getResourceDraft(botId, docId)));
        if (!draft) return false; // Can't possibly be edited then

        const original = JSON.parse(JSON.stringify(this.getResource(botId, docId)));
        if (!original) {

          /**
           * Edge case: no resources are loaded, but the draft for edits to an
           * existing resource is present. In this case, you'd need the original
           * either way to compare to, to know if the draft is modified or not.
           */

          return true; // New doc is always modified
        }

        const originalProps = fields.reduce((acc: Record<string, any>, field) => {
          acc[field] = get(original, field);
          return acc;
        }, {});
        const draftProps = fields.reduce((acc: Record<string, any>, field) => {
          acc[field] = get(draft, field);
          return acc;
        }, {});

        return !isEqual(draftProps, originalProps);
      };
    },
  },
  actions: {
    /**
     * @typedef {Object} LoadRagResourcesFilters
     * @prop {?string[]} [channelIds] Optional: Limit results to resources in the given channels. If empty, it is ignored
     * @prop {?string[]} [statuses] Optional: Limit results to resources that has one of the given statuses. If empty, it is ignored
     * @prop {?string[]} [resourceIds] Optional: An array of resource Object IDs that are queried and returned. If empty, it is ignored
     */
    /**
     * @typedef {Object} LoadRagResourcesOptions
     * @prop {?boolean} [withContent=false] Whether to also include the content for the resource
     * @prop {?boolean} [withConnections=false] Whether to also include which agents uses the resource
     * @prop {?boolean} [withDeleted=false] If true: Also include resources that are deleted, if you didn't already include it in `statuses`
     */
    /**
     * Fetches all resources for a bot from backend
     * @param {string} botId
     * @param {?boolean} [merge=true] If `true`, it will merge the new resources with the existing ones
     * @param {?LoadRagResourcesFilters} [filters]
     * @param {?LoadRagResourcesOptions} [options = {}]
     * @throws {Error} If something went wrong fetching the resources.
     * @returns {Promise<RAGResourceOverview[]>}
     */
    async fetchResources({ botId, merge, filters, options = {} }: { botId: string; merge?: boolean; filters?: any; options?: any }) {
      const result: any = await RAGServiceV2.getResources(botId, filters, options);

      if (result?.resources?.length) {
        this.setResources({ botId, merge, resources: result.resources });
      }

      this.setFlags(this.flags |= StateFlags.Initialized);
      return result?.resources ?? [];
    },
    /**
     * Fetches the Markdown content for a resource from backend
     * @param {string} botId
     * @param {string} docId Resource document ID. Depends on the resources already being fetched.
     * @param {string} [pageLabel="Page"] The "Page N" label to use when joining multiple pages of markdown
     * @returns {Promise<string | null>} The markdown content (could be empty string), or `null` if resource was not found
     */
    async fetchMarkdown({ botId, docId, pageLabel }: { botId: string; docId: string; pageLabel?: string }) {
      const resource = this.resources[botId]?.[docId];
      if (!resource || !resource.ressourceId) return null;

      const array: any = await RAGServiceV2.getRAGResourceMarkdown({ botId: botId, resourceId: resource.ressourceId });
      const abstract = array?.[0]?.abstract;
      if (abstract) {
        this.setResources({
          botId,
          resources: [{ _id: docId, abstract }],
          merge: true,
        });
      }
      let markdown = parseMarkdownArrayToString(array, {
        pageIdentification: true,
        pageText: pageLabel ?? 'Page',
        /**
         * Separator must be space, else tables split across documents will break.
         * @see https://github.com/knowhereto/moin-hub/issues/1565
         */
        separator: ' ',
      });

      if (resource.type === 'QNA' && !!markdown) {
        /**
         * Special handling:
         * QNA will have title inside its body. It must be extracted out into an `answer` property.
         * Question:\n Question here\n\n Answer:\n Answer here...
         */
        const split = markdown.split('\n\n Answer:\n');
        const data = {
          question: split[0].replace('Question:\n', '').trim() ?? '',
          // Re-construct the rest of the array, in the odd-case user literally had `\n\n Answer:\n` in their answer
          answer: split.slice(1).join('\n\n Answer:\n')?.trim() ?? '',
        };

        markdown = data.answer;
        // Set title too since we have it
        if (data.question) {
          set(resource, TitlePath.QNA, data.question);
          set(resource, TitlePath.QNAFallback, data.question);
          this.setResources({ botId, resources: [resource] });
        }
      }

      this.setMarkdown({ botId, docId, markdown });
      return markdown;
    },
    /**
     * Publishes a new document, and caches it in the `resources`
     * @param {string} botId
     * @param {string} channelId
     * @param {RAGResourceDraftDocument} resource
     * @param {RagIntent[]} connectToIntents
     * @returns {Promise<{resource: RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA | RagTABLE, intents: Object[]}>} The new full resource document from API
     */
    async publishNewResource({ botId, channelId, resource, connectToIntents }: { botId: string; channelId: string; resource: any; connectToIntents: any[] }) {
      if (ResourceDraftType.NEW_DRAFT !== resource._type) {
        console.error(`Expected resource to be a ${ResourceDraftType.NEW_DRAFT}, got ${resource._type}.`);
      }

      // TODO re-evaluate if necessary if Mongo stores it too
      if (resource.type === 'QNA') {
        // Special handling - Keep copy of title outside `content`, so it's instantly available
        set(resource, TitlePath.QNAFallback, get(resource, TitlePath.QNA, 'Question & Answer'));
      }

      const result: any = await RAGServiceV2.addNewResource(botId, channelId, resource, connectToIntents);
      if (result.status !== 'ok') return null;

      result.resource._type = ResourceDraftType.PUBLISHED;
      this.setResources({ botId, resources: [result.resource] });

      const intentsStore = useIntentsStore();
      const botsStore = useBotsStore();
      for (const intent of result.intents) {
        const fullIntent = intentsStore.intent(intent.intent);
        if (!fullIntent) continue;


        // The API takes 'null' for the default channel, but intent state is keyed by the real channel ID
        const actualChannelId = !channelId || channelId === 'null'
          ? botsStore.currentChannels?.[0]?.channelId || botsStore.currentBot?.channels?.[0]?.channelId || 'null'
          : channelId;
        // When Resource was added and connected to some intent. As response that intent (f.e. 'faq_general_faq_topic') will come in response with state as:
        // { active, forward, state } and NOT as { [channel_one]: { active, forward, state }, [channel_two]: { active, forward, state } }
        // ! thats why we need to ensure that we are not replacing whole state object here
        let state: any = typeof fullIntent.state === 'object' ? fullIntent.state : {};
        // Destruct states if other channels provided, otherwise just set the state for the single channel
        if (typeof intent.state === 'object' && intent.state[actualChannelId]) state = { ...state, ...intent.state };
        else state[actualChannelId] = intent.state;

        const newIntentState = { ...fullIntent, ...intent, state };
        intentsStore.setIntent(newIntentState);
      }

      return {
        resource: result.resource,
        intents: result.intents,
      };
    },
    /**
     * Perform an update on an existing document, and caches new state in `resources`
     * @template {RAGResourceDraftDocument | RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA} InputResource
     * @param {string} botId
     * @param {InputResource} resource The whole resource
     * @returns {Promise<InputResource>} The resource you passed in
     */
    async updateExistingResource({ botId, resource }: { botId: string; resource: any }) {
      if (resource.type === 'QNA') {
        // Special handling - Keep copy of title outside `content`, so it's instantly available
        set(resource, TitlePath.QNAFallback, get(resource, TitlePath.QNA, 'Question & Answer'));
      }

      const publishedVersion: any = await RAGServiceV2.updateResource(botId, resource._id, resource);
      if (!publishedVersion) return;

      const draftVersion = JSON.parse(JSON.stringify(publishedVersion));
      draftVersion._type = resource._type;

      // Store the published version
      publishedVersion._type = ResourceDraftType.PUBLISHED;
      this.setResources({ botId, resources: [publishedVersion] });

      // Update the draft
      if ([ResourceDraftType.DRAFT, ResourceDraftType.NEW_DRAFT].includes(draftVersion._type)) {
        this.updateDraft({ botId, draft: draftVersion });
      }

      return draftVersion;
    },
    /**
     * Update only specific parts of a resource. Does not update local cache.
     * @template {RAGResourceDraftDocument | RagPDF | RagWEBPAGE | RagMARKDOWN | RagQNA} InputResource
     * @param {string} botId
     * @param {string} docId
     * @param {Partial<InputResource>} partialResource The fields and values to update
     * @returns {Promise<void>}
     */
    updateResourcePartial({ botId, docId, partialResource }: { botId: string; docId: string; partialResource: any }) {
      return RAGServiceV2.updateResourceProperties(botId, docId, partialResource);
    },
    /**
     * Assigns intents to a resource, per channel
     * @param {string} botId
     * @param {string} docId
     * @param {RagIntent[]} intents
     * @returns {Promise<void>}
     */
    async setAssignedIntents({ botId, docId, intents }: { botId: string; docId: string; intents: any[] }) {
      await RAGServiceV2.setAssignedIntents(botId, docId, intents);

      const cached = this.getResource(botId, docId);
      if (cached) {
        cached.intents = intents;
        this.setResources({ botId, resources: [cached] });
      }
    },
    /**
     * @param {string} botId Live bot ID
     * @param {string} resourceId Resource Object ID
     * @param {string} intentName The technical name for the resource
     * @param {string|null} channelId The ID of the channel
     * @param {'staging'|'live'} environment Whether to apply the change to live or staging
     * @param {boolean} connected Whether the resource and the agent should be connected or not
     * @returns {Promise<null | object>} The intent that was updated, in its new state, or `null` if it failed
     */
    async setResourceAssignment({ botId, resourceId, intentName, channelId, environment, connected }: { botId: string; resourceId: string; intentName: string; channelId: string | null; environment: 'staging' | 'live'; connected: boolean }) {
      let result: any;
      try {
        result = await RAGServiceV2.setResourceAssignment(
          botId,
          resourceId,
          intentName,
          channelId,
          environment,
          connected
        );
      } catch (err: any) {
        console.error(err);
        useHubUiStore().addAlert(
          {
            message: err.message || 'Failed to toggle resource',
            type: 'error',
          },
        );
        return null;
      }

      // Update the resource itself. API returns the new resource state with all connected intents
      this.setResources({
        botId,
        resources: [result.resource],
      });

      // We got the new resources state for the intent, so we can update it
      const intentsStore = useIntentsStore();
      const currentIntent = intentsStore.intent(intentName);
      if (!currentIntent) return;
      currentIntent.resources = result.resources;

      intentsStore.setIntent({ intent: currentIntent });

      return currentIntent;
    },
    /**
     * Starts an interval that checks for changes to a resource's status.
     * Once the status is changed, or it has been too long, the interval is cleared.
     * If the status is changed, it will update the resource with the new status.
     * @param {string} botId
     * @param {string} docId The ID of the resource to start checking
     * @returns {void}
     */
    startStatusChangeChecker({ botId, docId }: { botId: string; docId: string }) {
      const originalStatus = this.getResource(botId, docId)?.status;
      if (!originalStatus) return;

      // Start background task
      (async () => {
        // Function to execute once it detects a change in status
        const changedCb = ({ botId, docId, status }: { botId: string; docId: string; status: string }) => {
          const resource = this.getResource(botId, docId);
          if (resource) {
            resource.status = status;
            this.setResources({ botId, resources: [resource] });
          }
        };

        await refreshResourceState({
          changedCb,
          botId,
          docId,
          originalStatus,
          maxTries: 12,
          timeoutMultiplier: [2, 2, 2],
        });
      })();
    },
    /**
     * Create a new action for the bot's RAG system.
     * @param {string} botId
     * @param {RAGActionPayload} payload
     * @returns {Promise<{ success: boolean, ragActions?: IntentRagActions }>}
     */
    async publishNewAction({ botId, payload }: { botId: string; payload: any }) {
      const result: any = await RAGServiceV2.addNewAction(botId, payload);

      if (result.error) {
        useHubUiStore().addAlert({
          message: result.message || 'An unknown error occurred when trying to create a new action',
          type: 'error',
        });
        return { success: false }
      }

      return {
        success: true,
        ragActions: result.ragActions,
      };
    },
    /**
     * Deletes a specific action from the bot's RAG system.
     * @param {string} botId The ID of the bot
     * @param {string} intent The intent technical name it is found in
     * @param {string} channelId The ID of the channel the action is in
     * @param {string} id The ID of the action
     * @returns {Promise<{success: boolean, ragActions?: IntentRagActions}>}
     */
    async deleteAction({ botId, intent, channelId, id }: { botId: string; intent: string; channelId: string; id: string }) {
      const result: any = await RAGServiceV2.deleteAction(botId, intent, id, channelId);

      if (result.error) {
        useHubUiStore().addAlert({
          message: result.message || 'An unknown error occurred when trying to delete an action',
          type: 'error',
        });
        return { success: false };
      }

      return {
        success: true,
        ragActions: result.ragActions,
      };
    },
    /**
     * Converted from legacy mutation `setFlags`.
     */
    setFlags(flags: number) {
      this.flags = flags;
    },
    /**
     * Creates a new resource draft, for brand-new documents
     *
     * Converted from legacy mutation `createNewDraft`.
     * @template {Object} ResourceDraft
     * @template {string} ResourceDraftId
     *
     * @param {string} botId
     * @param {ResourceDraft} resource
     * @param {?ResourceDraftId} [id] ID of the draft.
     * Created for you if not provided.
     */
    createNewDraft({ botId, resource, id }: { botId: string; resource: any; id?: string }) {
      if (!id) id = Math.random().toString(36).slice(2);
      resource._id = id;
      resource._type = ResourceDraftType.NEW_DRAFT;

      ensureProp(this.resourceDrafts, botId);
      this.resourceDrafts[botId][id] = resource;
    },
    /**
     * Converts an existing resource into a draft for editing purposes.
     * Stores a new `resourceDraft`, and keeps the original resource in `resources`
     * for comparisons and restoring.
     *
     * Converted from legacy mutation `convertToDraft`.
     * @param {string} botId
     * @param {{_id: string} & Object} resource
     */
    convertToDraft({ botId, resource }: { botId: string; resource: any }) {
      ensureProp(this.resourceDrafts, botId);
      const draft = JSON.parse(JSON.stringify(resource));
      draft._type = ResourceDraftType.DRAFT;
      this.resourceDrafts[botId][resource._id] = draft;

      // Make sure the original is also stored in resources
      commitResources(this, botId, { ...this.resources[botId], [resource._id]: resource });
    },
    /**
     * Removes a resource draft
     *
     * Converted from legacy mutation `removeDraft`. Legacy used `this.commit('knowledgeBase/autoCleanupDrafts')`
     * (chained commit); now a direct local action call.
     * @param {string} botId
     * @param {string} docId
     */
    removeDraft({ botId, docId }: { botId: string; docId: string }) {
      ensureProp(this.resourceDrafts, botId);
      delete this.resourceDrafts[botId][docId];
      this.autoCleanupDrafts();
    },
    /**
     * Updates an existing draft, or creates a new one if it does not exist yet.
     * For creation, you would ideally use `convertToDraft` or `createNewDraft` instead.
     *
     * Converted from legacy mutation `updateDraft`.
     * @param {string} botId
     * @param {{_id: string, updatedAt?: Date} & Object} draft The new state of the draft
     */
    updateDraft({ botId, draft }: { botId: string; draft: any }) {
      ensureProp(this.resourceDrafts, botId);
      draft.updatedAt = new Date();
      this.resourceDrafts[botId][draft._id] = draft;
    },
    /**
     * Adds new resource(s) to the bot, overwriting any conflicting ones
     *
     * Converted from legacy mutation `setResources`.
     * @param {string} botId
     * @param {?boolean} [merge=false] If a resource exists, it will merge the new state into the old instead of replacing the entire object
     * @param {RAGResourceOverview[] | ({_id: string} & Object)[]} resources
     */
    setResources({ botId, resources, merge }: { botId: string; resources: any[]; merge?: boolean }) {
      if (!resources) return;

      const botResources = { ...this.resources[botId] };
      let changed = !(botId in this.resources);

      for (let resource of resources) {
        if (!resource || !resource._id) {
          console.warn('Trying to set resource without an ID. Skipping...', resource);
          continue;
        }

        resource._type = ResourceDraftType.PUBLISHED;
        const existing = botResources[resource._id];

        if (merge && existing) {
          // Merge the new state into a copy of the old one; skip if nothing changed, so polling
          // (e.g. the KB page's 5s refresh of learning resources) doesn't re-render every consumer
          const merged = lodashMerge({}, existing, resource);
          if (isEqual(merged, existing)) continue;
          resource = merged;
        } else if (resource === existing) {
          // Edited in place by the caller: needs a new identity, or computeds won't pick it up
          resource = { ...resource };
        }

        botResources[resource._id] = resource;
        changed = true;
      }

      if (changed) commitResources(this, botId, botResources);
    },
    /**
     * Shallow-merges changed fields into stored resources, in a single update.
     * Use this instead of writing into a resource object, which would not re-render anything.
     * Resources that are not loaded are skipped.
     * @param {string} botId
     * @param {{docId: string, changes: Object}[]} patches
     */
    patchResources({ botId, patches }: { botId: string; patches: Array<{ docId: string; changes: Record<string, any> }> }) {
      const botResources = { ...this.resources[botId] };
      let changed = false;

      for (const { docId, changes } of patches) {
        if (!botResources[docId]) continue;
        botResources[docId] = { ...botResources[docId], ...changes };
        changed = true;
      }

      if (changed) commitResources(this, botId, botResources);
    },
    /**
     * Removes one or more resource(s) from the bot
     *
     * Converted from legacy mutation `removeResources`. Legacy used `this.commit('knowledgeBase/autoCleanupDrafts')`
     * (chained commit); now a direct local action call.
     * @param {string} botId
     * @param {string[]} docIds Resource Object IDs
     */
    removeResources({ botId, docIds }: { botId: string; docIds: string[] }) {
      const botResources = { ...this.resources[botId] };
      docIds.forEach(resourceId => {
        delete botResources[resourceId];
      });
      commitResources(this, botId, botResources);

      this.autoCleanupDrafts();
    },
    /**
     * Marks resources as to-be-deleted (draft).
     * Puts them into a deletion queue you can later remove them from again.
     * When the deletion process is initiated, it will use this queue.
     *
     * Converted from legacy mutation `markDeleteResources`.
     * @param {string} botId
     * @param {string[]} docIds Resource Object IDs
     */
    markDeleteResources({ botId, docIds }: { botId: string; docIds: string[] }) {
      this.queuedDeletions.push(
        ...docIds.map(docId => ({ botId, docId }))
      );
    },
    /**
     * Un-marks resources as not to be deleted after all (un-draft).
     * Removes them from the deletion queue, so that they don't get deleted
     * when you start processing deletions.
     *
     * Converted from legacy mutation `unmarkDeleteResource`.
     * @param {string} botId
     * @param {string[]} docIds Resource Object IDs
     */
    unmarkDeleteResource({ botId, docIds }: { botId: string; docIds: string[] }) {
      this.queuedDeletions = this.queuedDeletions
        .filter(d => !(d.botId === botId && docIds.includes(d.docId)))
    },
    /**
     * Sets/overwrites the Markdown content for a resource
     *
     * Converted from legacy mutation `setMarkdown`.
     * @param {string} botId
     * @param {string} docId The resource document ID
     * @param {string} markdown Markdown content
     */
    setMarkdown({ botId, docId, markdown }: { botId: string; docId: string; markdown: string }) {
      // Get the resource
      const resource = this.resources[botId]?.[docId];
      if (!resource) return;

      // Use the path to know where to put its Markdown
      let markdownPath = (MarkdownPath as any)[resource.type];
      if (!markdownPath) markdownPath = 'markdown'; // Fallback; ideally, it is always defined in `MarkdownPath`!

      /**
       * Set the Markdown at that path in a copy of the resource, copying every object along it
       * markdownPath = "some.nested.path"
       * resource: {some: {nested: {path: "Markdown content"}}}
       */
      const parts = markdownPath.split(".");
      const updated = { ...resource };
      let ref = updated;
      for (let i=0; i<parts.length; i++) {
        // Ensure path
        if (parts[i+1]) {
          ref[parts[i]] = { ...ref[parts[i]] };
          ref = ref[parts[i]];
          continue;
        }

        // Set the content
        ref[parts[i]] = markdown;
      }

      commitResources(this, botId, { ...this.resources[botId], [docId]: updated });
    },

    /**
     * Gets rid of empty localStorage draft containers for bots
     *
     * Converted from legacy mutation `autoCleanupDrafts`.
     * @private
     */
    autoCleanupDrafts() {
      for (const botId in this.resourceDrafts) {
        if (Object.keys(this.resourceDrafts[botId]).length === 0) {
          delete this.resourceDrafts[botId];
        }
      }
    },
  },
  vuexPersist: {
    local: ['resourceDrafts', 'markdownDrafts'],
  },
});

/**
 * Stores the new resource map of a bot, which is what notifies consumers of `resources` (a
 * `shallowRef`). The top-level map gets a new identity too: computeds only propagate a change when
 * their value's identity changes, so e.g. `computed(() => store.resources[botId])` must not see the
 * same object again.
 * @param store The knowledge base store
 * @param {string} botId
 * @param {Record<string, Object>} botResources The complete new map of the bot's resources
 */
function commitResources(store: { resources: KnowledgeBaseState['resources'] }, botId: string, botResources: Record<string, any>) {
  store.resources = { ...store.resources, [botId]: botResources };
}

/**
 * Simply ensures that the property key exists in the object.
 * Sets an empty object if it does not.
 * @param {Object} obj
 * @param {string} prop
 */
const ensureProp = (obj: any, prop: string) => {
  if (!(prop in obj)) obj[prop] = {};
};

/**
 * Refreshes the status of a specific resource element by tracking its status.
 *
 * @param {Object} options - The options for refreshing the resource state.
 * @param {({botId: string, docId: string, status: string}) => void} options.changedCb - The callback function to call when the status changes.
 * @param {string} options.botId - The ID of the bot
 * @param {string} options.originalStatus - The original status of the resource, used to compare against.
 * @param {string} options.docId - The Object ID of the resource to refresh.
 * @param {number} [options.maxTries=3] - The maximum number of retries before giving up. Defaults to 3.
 * @param {number} [options.timeout=2500] - The initial timeout in milliseconds before retrying. Defaults to 2500ms.
 * @param {number} [options.timeoutMultiplier=[1]] - The multiplier to apply to the timeout for each retry. Defaults to 1.
 * @param {number} [options._currTry=0] - Internal counter for the current retry attempt (not for user consumption).
 * @returns {Promise<void>} A promise that resolves when the refresh is complete or fails after retries.
 *
 * @example
 * // Example usage:
 * const originalStatus = 'uploading';
 * const changedCb = ({botId, docId, status}) => {
 *   console.log(`Status of ${docId} changed to ${status} in ${botId}!`);
 * }
 * await refreshResourceState({ changedCb, botId, originalStatus, docId: 'id', maxTries: 5, timeout: 2500, timeoutMultiplier: [2,3] });
 *
 * // Explanation:
 * // - maxTries: 5 means the function will retry up to 5 times.
 * // - timeout: 2500ms is the initial delay before the first retry.
 * // - timeoutMultiplier: [2,3] means the timeout will double after first try and x3 after second.
 * //
 * // Total duration calculation:
 * // - 1st try: 2500ms
 * // - 2nd try: 2500ms * 2 = 5000ms
 * // - 3rd try: 5000ms * 3 = 15000ms
 * // - 4th try: 15000ms * 1 = 15000ms // x1 because timeoutMultiplier is out of range
 * // - 5th try: 15000ms * 1 = 15000ms // x1 because timeoutMultiplier is out of range
 * //
 * // Total time = 2500ms + 5000ms + 15000ms + 15000ms + 15000ms = 52500ms (52.5 seconds) | 0min 52.5sec
 */
async function refreshResourceState({ changedCb, botId, originalStatus, docId, maxTries = 3, timeout = 2500, timeoutMultiplier = [1], _currTry = 0 }: { changedCb: (arg: { botId: string; docId: string; status: string }) => void; botId: string; originalStatus: string; docId: string; maxTries?: number; timeout?: number; timeoutMultiplier?: number[]; _currTry?: number }) {
  console.log("[%s] CHECKING STATUS AGAIN", _currTry);
  ++_currTry;
  // Termination safeguard: stop retrying when there's no doc to poll or we've exhausted our tries.
  if (!docId || _currTry > maxTries) return;

  let timeoutMs = timeout;

  if (Array.isArray(timeoutMultiplier) && timeoutMultiplier.length > 0) {
    const multiplier = Number(timeoutMultiplier[_currTry - 1]) || 1; // If out of range, then x1;
    timeoutMs = timeout * multiplier;
  }

  await new Promise<void>((resolve: any) => {
    setTimeout(async () => {

      // Legacy called `RAGServiceV2.getByParams(...)` too (src/store/modules/knowledgeBase.ts:878),
      // but no such method exists on the rag_v2 service — a pre-existing latent bug (this call would
      // throw at runtime). Cast preserves the exact legacy call target rather than substituting a
      // real method and changing behavior.
      const data: any = await (RAGServiceV2 as any).getByParams({ botId, id: docId });
      if (!data || !data.status) return resolve();  // STOP Tracking

      // Keep tracking status if identical
      if (data.status === originalStatus) {
        return resolve(
          // Pre-existing bug kept for behavior parity: `id` and `ressourceId` are undefined here,
          // so this branch throws a ReferenceError at runtime instead of recursing.
          // @ts-expect-error -- TS2304 on the undefined identifiers below
          refreshResourceState({ changedCb, botId, originalStatus, id, ressourceId, maxTries, timeout: timeoutMs, timeoutMultiplier, _currTry })
        );
      }

      changedCb({
        botId,
        docId,
        status: data.status
      });

      return resolve(); // STOP Tracking
    }, timeoutMs);
  })
}
